import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {Opportunity} from '../interfaces/opportunity.interface';
import {DataService} from '../../services/data.service';
import {map} from 'rxjs/operators';
import {firestoreDateToJsDate} from '../../shared/utils/utils';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {FollowUp} from '../interfaces/follow-up.interface';
import {Moment} from 'moment-timezone';
import {Company} from '../interfaces/company.interface';
import {ConfigService} from '../../services/config.service';



const LOCAL_STORAGE_FILTER_KEY = 'opportunitiesListFilter';
const LOCAL_STORAGE_PAGING_PREFERENCE = 'opportunitiesListPagingPreference';

@Injectable({
    providedIn: 'root'
})
export class OpportunityService {
    private pendingAction: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public pendingAction$: Observable<boolean> = this.pendingAction.asObservable();
    constructor(
        private dataService: DataService,
        private configService: ConfigService
    ) {
    }

    setPendingAction() {
        this.pendingAction.next(true);
    }

    unsetPendingAction() {
        this.pendingAction.next(false);
    }

    retrieveFilterFromLocalStorage(): string | null {
        return localStorage.getItem(LOCAL_STORAGE_FILTER_KEY) ?? null;
    }

    storeFilterInLocalStorage(filterString: string | null) {
        console.log('storing filter: ', filterString);
        if (filterString !== null) {
            localStorage.setItem(LOCAL_STORAGE_FILTER_KEY, filterString);
        } else {
            localStorage.removeItem(LOCAL_STORAGE_FILTER_KEY);
        }
    }

    retrievePageSizePreferences(): number {
        const storedValue = localStorage.getItem(LOCAL_STORAGE_PAGING_PREFERENCE);
        return storedValue?.length ? parseInt(storedValue, 0) : 10;
    }

    storePageSizePreferences(pageSize: number) {
        localStorage.setItem(LOCAL_STORAGE_PAGING_PREFERENCE, pageSize.toString());
    }

    getOpportunitiesList$(): Observable<Opportunity[]> {
        return this.dataService.getOpportunitiesList().pipe(
            map(ops => ops.map(o => this.convertOpportunityDatesFromFirestore(o)))
        );
    }

    getOpportunityByOpportunityId$(opportunityId: string): Observable<Opportunity | undefined> {
        return this.dataService.getOpportunityByOpportunityId(opportunityId).pipe(
            map(doc => {
                return !doc ? undefined : {
                    ...this.convertOpportunityDatesFromFirestore(doc),
                    id: doc.id
                };
            })
        );
    }

    private convertOpportunityDatesFromFirestore(o: Opportunity) {
        if (o) {
            o.createdAt = o.createdAt ? firestoreDateToJsDate(o.createdAt) : null;
            o.proposalSentOn = o.proposalSentOn ? firestoreDateToJsDate(o.proposalSentOn) : null;
            o.proposalRequested = o.proposalRequested ? firestoreDateToJsDate(o.proposalRequested) : null;
            o.nextFollowUpDate = o.nextFollowUpDate ? firestoreDateToJsDate(o.nextFollowUpDate) : null;
            o.followUpDate = o.followUpDate ? firestoreDateToJsDate(o.followUpDate) : null;
        }
        return o;
    }

    getFollowUpsList$(opportunityId: string): Observable<FollowUp[]> {
        return this.dataService.getFollowUpsListByOpportunityId(opportunityId).pipe(
            map(list => {
                return list.map(fu => {
                    fu.nextFollowUpDate = fu.nextFollowUpDate ? firestoreDateToJsDate(fu.nextFollowUpDate) : null;
                    fu.followUpDate = fu.followUpDate ? firestoreDateToJsDate(fu.followUpDate) : null;
                    fu.proposalSentOn = fu.proposalSentOn ? firestoreDateToJsDate(fu.proposalSentOn) : null;
                    fu.proposalRequested = fu.proposalRequested ? firestoreDateToJsDate(fu.proposalRequested) : null;
                    fu.createdAt = fu.createdAt ? firestoreDateToJsDate(fu.createdAt) : null;
                    return fu;
                });
            })
        );
    }

    getEmptyFollowUpForm() {
        return new UntypedFormGroup({
            id: new UntypedFormControl(null),
            opportunityId: new UntypedFormControl(null),
            latest: new UntypedFormControl(true),
            previous: new UntypedFormControl(null),
            status: new UntypedFormControl(null),
            opportunityStage: new UntypedFormControl(null),
            lostBecause: new UntypedFormControl(null),
            revisionNumber: new UntypedFormControl(null),
            proposalSentOn: new UntypedFormControl(null),
            proposalRequested: new UntypedFormControl(null),
            followUpDate: new UntypedFormControl(null),
            nextFollowUpDate: new UntypedFormControl(null),
            nextSteps: new UntypedFormControl(null),
            potentialClosureQuarter: new UntypedFormControl(null),
            potentialClosureYear: new UntypedFormControl(null),
            currency: new UntypedFormControl(null),
            exchangeRate: new UntypedFormControl(null),
            forecastAmountITA: new UntypedFormControl(null),
            forecastAmountUSA: new UntypedFormControl(null),
            createdBy: new UntypedFormControl(null),
            createdAt: new UntypedFormControl(null),
            dummy: new UntypedFormControl(false)
        });
    }

    updateForecastAmountsValue(obj: Opportunity | FollowUp) {
        switch (obj.currency) {
            case 'EUR':
                obj.forecastAmountUSA = (obj.forecastAmountITA !== null) ?
                    obj.forecastAmountITA * obj.exchangeRate : null;
                break;
            case 'USD':
                obj.forecastAmountITA = (obj.forecastAmountUSA !== null) ?
                    obj.forecastAmountUSA / obj.exchangeRate : null;
                break;
        }
    }

    transformDatesBeforeSave(obj: Opportunity | FollowUp) {
        if (obj.followUpDate) {
            obj.followUpDate = (obj.followUpDate instanceof Date) ? obj.followUpDate :
                (<Moment>obj.followUpDate).toDate();
        }

        if (obj.proposalSentOn) {
            obj.proposalSentOn = (obj.proposalSentOn instanceof Date) ? obj.proposalSentOn :
                (<Moment>obj.proposalSentOn).toDate();
        }

        if (obj.proposalRequested) {
            obj.proposalRequested = (obj.proposalRequested instanceof Date) ? obj.proposalRequested :
                (<Moment>obj.proposalRequested).toDate();
        }

        if (obj.nextFollowUpDate) {
            obj.nextFollowUpDate = (obj.nextFollowUpDate instanceof Date) ? obj.nextFollowUpDate :
                (<Moment>obj.nextFollowUpDate).toDate();
        }
    }

    deleteOpportunity(opportunity: Opportunity) {
        console.log('Going to delete opportunity', opportunity);
        return this.dataService.deleteOpportunity(opportunity);
    }

    changeOpportunityName(opportunity: Opportunity, newName: string) {
        console.log('Going to change opportunity name: ', newName);
        return this.dataService.changeOpportunityName(opportunity, newName);
    }

    changeOpportunityCompany(opportunity: Opportunity, company: Company) {
        console.log('Going to change opportunity company: ', company);
        const cosmoId = company.cosmoId ?? (company.custom_fields ? this.extractCosmoIdFromCompany(company) : null);
        return this.dataService.changeOpportunityCompany(opportunity, company, cosmoId);
    }

    deleteFollowUp(followUp: FollowUp) {
        return this.dataService.deleteFollowUp(followUp);
    }

    assignSalesManager(opportunity: Opportunity, email: string) {
        return this.dataService.assignSalesManager(opportunity, email);
    }

    getCompanyDetails$(id: number) {
        return this.dataService.getCompanyDetailsById(id).pipe(
            map(result => result['company'])
        );
    }

    extractCosmoIdFromCompany(company: Company): string {
        const cosmoKey = this.configService.settings.value.value.prosperworks['cfCosmoId'];
        const cosmoId = cosmoKey ?
            company.custom_fields.find(f => f.custom_field_definition_id === parseInt(cosmoKey, 0)).value : null;

        return cosmoId ?? null;
    }
}
